import { StandardEvents } from '@shopify/events';

/** V36.23: successful-add confirmation and full-page cart usability.
 * No checkout redirect or cart writes here: validation and vehicle persistence
 * vehicle persistence remain responsible for submitting the order information.
 */
const dialog = document.getElementById('dj-cart-added');
let returnFocus = null;

function showAdded(event, cart, detail, sourceModal, origin) {
  if (!dialog || detail?.didError) return;
  const added = Number(detail?.itemCount ?? event.lines?.reduce((n, line) => n + Number(line.quantity || 0), 0));
  if (!Number.isFinite(added) || added <= 0) return;
  const ids = (event.lines || []).map(line => String(line.merchandiseId || '').split('/').pop());
  const items = (detail?.items || []).filter(item => ids.includes(String(item.variant_id ?? item.id)));
  const item = items[0];
  const title = items.length === 1 ? item.product_title || item.title : 'Votre sélection de jantes';
  dialog.querySelector('[data-dj-added-title]').textContent = title || 'Vos jantes';
  dialog.querySelector('[data-dj-added-quantity]').textContent = `${added} ${added > 1 ? 'jantes ajoutées' : 'jante ajoutée'}`;
  const count = Number(cart?.totalQuantity);
  dialog.querySelector('[data-dj-added-count]').textContent = Number.isFinite(count)
    ? `Votre panier contient ${count} ${count > 1 ? 'jantes' : 'jante'}.` : 'Vos articles sont enregistrés dans le panier.';
  const image = dialog.querySelector('[data-dj-added-image]');
  const src = typeof item?.image === 'string' ? item.image : item?.featured_image?.url;
  image.hidden = !src || items.length !== 1;
  image.removeAttribute('src');
  if (!image.hidden) image.src = src;
  const open = () => {
    if (!dialog.isConnected) return;
    if (!dialog.open) {
      returnFocus = origin;
      dialog.showModal();
    }
  };
  // Quick-add owns its modal's close/focus lifecycle; never stack two modals.
  if (sourceModal?.open) sourceModal.addEventListener('close', open, { once: true });
  else open();
}

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (event.action !== 'add' || !event.promise) return;
  const origin = document.activeElement;
  const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
  event.promise.then(({ cart, detail }) => showAdded(event, cart, detail, sourceModal, origin))
    .catch(() => { /* Product form already owns stock and network errors. */ });
});

dialog?.querySelectorAll('[data-dj-close-added]').forEach(button => {
  button.addEventListener('click', () => dialog.close());
});
dialog?.addEventListener('click', event => {
  if (event.target !== dialog) return;
  const rect = dialog.getBoundingClientRect();
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
});
dialog?.addEventListener('close', () => {
  if (returnFocus?.isConnected && !returnFocus.closest('[inert]')) returnFocus.focus({ preventScroll: true });
});
window.addEventListener('pageshow', event => { if (event.persisted && dialog?.open) dialog.close(); });
// Checkout may change line keys while the browser retains the old cart DOM.
// Restore a fresh cart (including the header count and unlocked controls) on
// back/forward-cache returns instead of reusing those stale rows and keys.
window.addEventListener('pageshow', event => {
  if (event.persisted && document.body.dataset.djPage === 'cart') window.location.reload();
});

// Professional drafts are scoped to the current cart and tab, like vehicle
// drafts. Returning from the catalogue or a section update must not erase them.
const professionalDrafts = new Map();
const draftKey = form => `dj-professional-v1:${form.dataset.djCartToken || ''}`;
function getDraft(form) {
  const key = draftKey(form);
  if (professionalDrafts.has(key)) return professionalDrafts.get(key);
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (value && typeof value === 'object') { professionalDrafts.set(key, value); return value; }
  } catch { /* Storage may be unavailable. */ }
  return null;
}
function rememberProfessional(form) {
  const value = {};
  for (const field of form.elements) {
    if (!field.matches?.('[data-dj-pro-field]')) continue;
    value[field.dataset.djProField] = field.type === 'checkbox' ? field.checked : field.value;
  }
  const key = draftKey(form);
  professionalDrafts.set(key, value);
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* Keep memory draft. */ }
}
function restoreProfessional() {
  for (const form of document.querySelectorAll('.cart-page .cart-form')) {
    const draft = getDraft(form);
    if (!draft) continue;
    for (const field of form.elements) {
      const name = field.dataset?.djProField;
      if (!name || !(name in draft) || field === document.activeElement) continue;
      if (field.type === 'checkbox') field.checked = draft[name] === true;
      else field.value = String(draft[name]);
    }
  }
}
for (const type of ['input', 'change']) document.addEventListener(type, event => {
  const field = event.target;
  if (field.matches?.('[data-dj-pro-field]') && field.form) rememberProfessional(field.form);
});
// Capture final autofill values as well as typed input before leaving the page.
window.addEventListener('pagehide', () => {
  document.querySelectorAll('.cart-page .cart-form').forEach(rememberProfessional);
});

let checkoutPointerDown = false;
document.addEventListener('pointerdown', event => {
  if (event.target.closest?.('[data-dj-checkout]')) checkoutPointerDown = true;
}, true);
for (const type of ['pointerup', 'pointercancel']) document.addEventListener(type, () => {
  if (!checkoutPointerDown) return;
  // Finish the click before moving the CTA when a text field loses focus.
  setTimeout(() => { checkoutPointerDown = false; refreshPage(); }, 0);
}, true);
let observedCta = null;
const mobile = window.matchMedia('(max-width: 749px)');
const resizeObserver = new ResizeObserver(() => updateMobileSpace());
function updateMobileSpace() {
  if (checkoutPointerDown) return;
  const cta = document.querySelector('.cart-page [data-dj-cart-cta]');
  if (cta !== observedCta) {
    if (observedCta) resizeObserver.unobserve(observedCta);
    observedCta = cta;
    if (cta) resizeObserver.observe(cta);
  }
  const editing = document.activeElement?.matches('input:not([type="checkbox"]):not([type="button"]):not([type="submit"]), textarea') &&
    Boolean(document.activeElement?.closest('.cart-page'));
  const keyboard = Boolean(mobile.matches && editing);
  document.body.toggleAttribute('data-dj-cart-keyboard', keyboard);
  const fixed = cta && mobile.matches && !keyboard;
  const space = fixed ? `${Math.ceil(cta.getBoundingClientRect().height) + 20}px` : '0px';
  if (document.body.style.getPropertyValue('--dj-cart-bottom-space') !== space) document.body.style.setProperty('--dj-cart-bottom-space', space);
}
let scheduled = false;
function refreshPage() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => { scheduled = false; restoreProfessional(); updateMobileSpace(); });
}
const page = document.getElementById('MainContent');
if (page && document.body.dataset.djPage === 'cart') {
  new MutationObserver(refreshPage).observe(page, { childList: true, subtree: true });
  for (const type of ['focusin', 'focusout']) document.addEventListener(type, refreshPage);
  window.visualViewport?.addEventListener('resize', refreshPage);
  window.addEventListener('resize', refreshPage);
  window.addEventListener('pageshow', refreshPage);
  refreshPage();
}
